import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import BrandsFeed from "@/components/BrandsFeed";
import StorefrontSearchStrip from "@/components/StorefrontSearchStrip";
import PageBackground from "@/components/PageBackground";
import Seo from "@/components/Seo";

export default function Coupons() {
  const [params, setParams] = useSearchParams();
  const [coupons, setCoupons] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const flash = params.get("flash") === "true";
  const q = params.get("q") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (flash) qs.set("flash", "true");
    if (q) qs.set("q", q);
    api.get(`/coupons?${qs}`).then((r) => {
      setCoupons(r.data);
      setLoading(false);
    });
  }, [category, flash, q]);

  const setCat = (slug) => {
    const p = new URLSearchParams(params);
    if (slug) p.set("category", slug);
    else p.delete("category");
    setParams(p);
  };

  return (
    <div className="relative">
      <Seo
        title={q ? `Search: ${q}` : flash ? "Flash sales — verified deals in Canada" : category ? `${cats.find((c) => c.slug === category)?.name || "Category"} coupons` : "All coupons — verified deals from Canadian brands"}
        description={q ? `Cyannect search results for "${q}" — verified coupons and cashback offers from Canadian brands.` : flash ? "Live flash sales on Cyannect — time-limited verified coupons from Canadian brands. Fresh drops daily." : "Browse verified coupons, cashback offers and flash sales from Canadian brands on Cyannect. Every deal is verified and every conversion is tracked."}
        path="/coupons"
        keywords="Canadian coupons, verified coupons, cashback offers, flash sales, Canadian brands"
      />
      <PageBackground variant="cyan-lav" />
      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
      <div className="mb-8">
        <div className="pf-chip pf-chip-cyan">Marketplace</div>
        <h1 className="font-display text-5xl md:text-6xl font-medium tracking-[-0.03em] mt-3 text-slate-900">
          {q ? `Results for "${q}"` : flash ? "Flash sales" : category ? cats.find((c) => c.slug === category)?.name || "Coupons" : "All coupons"}
        </h1>
      </div>

      {/* Storefront-first search (§47) — appears above coupon results when
          the shopper is searching by keyword. */}
      {q && <StorefrontSearchStrip q={q} />}

      {/* Shop by brand — storefront-aggregated view (§28-29). Only shows on
          the default feed to avoid noise on filtered/search views. */}
      {!q && !flash && !category && (
        <section className="mb-10" data-testid="coupons-brands-strip">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-2xl text-slate-900">Shop by brand</h2>
            <a href="/brands" className="text-sm text-cyan-700 font-semibold hover:underline" data-testid="brands-see-all">See all →</a>
          </div>
          <BrandsFeed limit={8} compact />
        </section>
      )}

      <div className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => setCat("")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${!category ? "pf-btn-primary" : "pf-btn-glass"}`}
          data-testid="filter-all"
        >
          All
        </button>
        {cats.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.slug)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${category === c.slug ? "pf-btn-primary" : "pf-btn-glass"}`}
            data-testid={`filter-cat-${c.slug}`}
          >
            {c.name}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500">Loading...</div>
      ) : coupons.length === 0 ? (
        <div className="py-24 text-center text-slate-500">No coupons found.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="coupons-grid">
          {coupons.map((c) => (
            <CouponCard key={c.id} coupon={c} />
          ))}
        </div>
      )}
      </div>
    </div>
  );
}
